import { useState } from "react";
import { Pressable, ActivityIndicator } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useRouter } from "expo-router";
import { updatePassword, EmailAuthProvider, linkWithCredential } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";

const Section = styled.View`padding: 20px;`;
const Label = styled.Text`font-size: 11px; font-weight: ${font.bold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; margin-top: 16px;`;
const Input = styled.TextInput`background-color: ${colors.white}; border: 1px solid ${colors.line}; border-radius: ${radius.md}px; padding: 12px 14px; font-size: 15px; color: ${colors.ink};`;
const ErrorText = styled.Text`color: ${colors.danger}; font-size: 13px; font-weight: ${font.semibold}; margin-top: 8px;`;
const SaveBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.card}px; padding: 16px; align-items: center; margin-top: 24px;`;
const SaveBtnText = styled.Text`color: #fff; font-size: 16px; font-weight: ${font.bold};`;

export default function SetPasswordScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { alertProps, alert } = useAlert();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const hasPassword = user?.providerData?.some((p) => p.providerId === "password") ?? false;

  const handleSave = async () => {
    if (!user || !newPassword.trim()) return;
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (hasPassword) {
        await updatePassword(user, newPassword);
      } else {
        const credential = EmailAuthProvider.credential(user.email!, newPassword);
        await linkWithCredential(user, credential);
      }
      await alert({
        title: "Password set!",
        emoji: "✅",
        message: "You can now sign in with your email and password on any device.",
        buttonLabel: "Done",
      });
      router.back();
    } catch (err: any) {
      setError(err.message || "Failed to set password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <Section>
          <Label style={{ marginTop: 0 }}>New password</Label>
          <Input
            placeholder="At least 6 characters"
            placeholderTextColor={colors.inkMuted}
            value={newPassword}
            onChangeText={setNewPassword}
            secureTextEntry
          />

          <Label>Confirm password</Label>
          <Input
            placeholder="Repeat password"
            placeholderTextColor={colors.inkMuted}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry
          />

          {error ? <ErrorText>{error}</ErrorText> : null}

          <Pressable
            onPress={handleSave}
            disabled={saving || !newPassword.trim()}
            style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
          >
            <SaveBtn style={{ opacity: !newPassword.trim() ? 0.5 : 1 }}>
              {saving ? <ActivityIndicator color="#fff" /> : <SaveBtnText>{hasPassword ? "Change password" : "Set password"}</SaveBtnText>}
            </SaveBtn>
          </Pressable>
        </Section>
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </KeyboardAwareScroll>
  );
}
